import { useTranslation } from 'react-i18next';
import { Box, HStack, Image, VStack } from 'native-base';
import { chunk, isEmpty, orderBy } from 'lodash';
import moment from 'moment';

import { Text } from '@pimm/base';
import { ResourceLoader } from '@app/components/shared';
import { useGetDashboardInfo } from '@app/features/delivery-monitor/hooks/get-delivery-monitor.hook';
import { useSiteConfig } from '@app/features/store-core';
import { WidgetBox, WidgetBoxProps } from '../widget-box';
import { useAppLocale } from '@pimm/common';

type WidgetDeliveryMonitorProps = WidgetBoxProps & {};
export const WidgetDeliveryMonitor = ({ ...props }: WidgetDeliveryMonitorProps) => {
  const { translate } = useAppLocale();
  const { siteConfig } = useSiteConfig();
  const hasDeliveryMonitor = siteConfig?.config?.dashboardConfig?.deliveryMonitor?.enabled;
  const deliveryStatus = useGetDashboardInfo(hasDeliveryMonitor ? siteConfig?.stopId : undefined);

  const calculateDuration = (lastModifiedOnUtc?: string) => {
    if (!lastModifiedOnUtc) return '0d 0h 0m';
    const now = moment.utc().utcOffset(siteConfig?.utcOffsetInMinutes ?? 0);
    const lastModified = moment.utc(lastModifiedOnUtc).utcOffset(siteConfig?.utcOffsetInMinutes ?? 0);
    const diff = moment.duration(now.diff(lastModified));
    return `${diff.days()}d ${diff.hours()}h ${diff.minutes()}m`;
  };

  const items = orderBy(deliveryStatus?.data?.subVendorStatuses, [item => item.status !== 'offline'], ['asc']);

  return (
    <WidgetBox hideTitle {...props}>
      <HStack alignItems="center" justifyContent="space-between" mt={-1}>
        <Text size="sm" fontWeight={700} color="gray.600" lineHeight="md" textTransform="uppercase" numberOfLines={1} ellipsizeMode="tail">
          {translate(props?.title, props?.titleTranslations)}
        </Text>

        {!!deliveryStatus?.data?.providerLogoUrl && (
          <Image
            source={{ uri: deliveryStatus?.data?.providerLogoUrl }}
            style={{ aspectRatio: 1, height: 30, width: 90 }}
            resizeMode="contain"
            resizeMethod="auto"
            alt={`${deliveryStatus?.data?.providerName} logo`}
          />
        )}
      </HStack>

      <ResourceLoader
        flex={1}
        minH="90px"
        isLoading={deliveryStatus?.isLoading}
        isEmpty={isEmpty(deliveryStatus?.data?.subVendorStatuses)}
        _text={{ size: 'sm' }}
      >
        <VStack space={1}>
          {chunk(items, 2).map((row, rowIndex) => (
            <HStack key={rowIndex} space={2}>
              {row.map(item => {
                const isOffline = item.status === 'offline';
                return (
                  <Box key={item.providerName} minH={43} flex={1}>
                    <HStack space={1} flex={1} alignItems="center" bgColor={isOffline ? 'error.500' : 'success.500'} rounded="lg" px={1}>
                      <Image
                        source={{ uri: item.providerLogoUrl }}
                        style={{ width: 25, height: 25 }}
                        resizeMode="contain"
                        resizeMethod="auto"
                        alt={`${item.providerName} logo`}
                      />
                      <VStack flex={1} space={0}>
                        <Text size="md" fontWeight={700} color="white">
                          {item?.providerName?.toUpperCase()}
                        </Text>

                        {isOffline && (
                          <Text size="sm" fontWeight={600} color="white">
                            {calculateDuration(item?.lastModifiedOnUtc)}
                          </Text>
                        )}
                      </VStack>
                    </HStack>
                  </Box>
                );
              })}
              {row.length === 1 && <Box flex={1} />}
            </HStack>
          ))}
        </VStack>
      </ResourceLoader>
    </WidgetBox>
  );
};
